import { UnilevelMember } from "../Unilevel/UnilevelInterfaces";
import firestore from "firebase-admin";
import { BinaryPoints } from "../System/SystemInterfaces";
import { Timestamp } from "firebase-admin/firestore";

export type userside = "left" | "right";

export interface NewUserData {
  email: string;
  username: string;
  password: string;
  passwordConfirm: string;
  enroller: string;
  binarySide: userside;
}

/**
 * Placement state on `users/{u}` (§6). The booleans are the QUERY key (the
 * "pendientes de colocar" view filters `positioned.unilevel == false`), so they
 * must always EXIST — Firestore `== null` does not match absent fields. Written
 * `false` at registration, flipped by placeMember, backfilled by the migration.
 * The timestamps are display/audit only; their null never gets queried.
 */
export interface PositionedFlags {
  unilevel: boolean;
  binary: boolean;
  unilevelAt: Date | firestore.firestore.Timestamp | null;
  binaryAt: Date | firestore.firestore.Timestamp | null;
}

export interface UserInfo {
  _usernamePK: string;
  _uid: string;
  username: string;
  email: string;
  nickname: string;
  pictureUrl: string;
  currentRank: string;
  enroller: string;
  // ⚠️ THIS TYPE LIES, KNOWINGLY, AND IT IS #559's TO FIX (§11 paso 1, el deploy del cutover).
  //
  // At runtime this is NULL for every unplaced user — the #558 sweep empties the
  // mirror for all 41,600 and it stays empty until a placement writes it back.
  // The honest type is `userside | null`.
  //
  // Making it honest here surfaces 15 unguarded live call-sites (PointsFunc x10,
  // GTRAcademy x3, GtrLite x2) that would then stop compiling — and those are the
  // "guardas anti-vacío en los 7 caminantes de cadena" that §11 paso 1 already
  // assigns to the cutover, in files #559 is rewriting right now. Fixing them from
  // this branch would collide with theirs on money paths, days before go-live.
  //
  // So the type stays wrong ON PURPOSE, and the exact 15 sites are filed on #565
  // as a GO-LIVE BLOCKER. Flip this to `userside | null` there: the compiler then
  // points at every one of them, which is the whole reason to do it.
  binarySide: userside;
  roles: string[];
  ranksCarrerData: any; //Map<string, RankCarrerData>;
  /** Original registration date (§6) — from Auth `creationTime` in the migration, `now` for new signups. */
  registeredAt?: Date | firestore.firestore.Timestamp;
  /** Absent on pre-reset docs until the migration backfills all ~36k (§6). */
  positioned?: PositionedFlags;
  /**
   * The BRIDGE across the dormants (§17 flow 3, restored — #559/#558).
   *
   * `uplines` is the OLD unilevel ancestry, nearest first, copied by the #558
   * sweep from `networkData/basic.uplines` BEFORE #557 prunes that doc. It is
   * the only surviving record of the pre-reset genealogy, and it is what lets a
   * chain walk cross a dormant whose live `enroller` the sweep blanked.
   *
   * Consulted ONLY when `enroller` is empty, and only until that member is
   * placed (placeMember writes them a live `enroller` + a node) — a one-shot
   * bridge per person, never a permanent source. It decides WHO to ask, never
   * where anyone hangs.
   *
   * `~` (126) sorts after `z` (122), so the field sinks to the bottom of the
   * Firestore console — the mirror of `_`, which floats to the top.
   */
  "~legacy"?: {
    uplines: string[];
  };
  /**
   * ACCOUNT ALIAS (#570): "this account IS `<canonical>` under another name."
   *
   * A founder holding two accounts is ONE PERSON, and the tree must say so. The
   * alternate account is TRANSPARENT: it never gets a node (placeMember refuses
   * it, even on a renewal), and every enroller-chain walk that reaches it
   * continues at the canonical account instead — resolved in ONE place,
   * `ancestorsOf()` (EnrollerChain.ts), which all four walks pass through.
   *
   * Written by the seed apply from the seed config (never hardcoded), and read at
   * runtime from here. `~legacy.uplines` is deliberately left alone: the alias
   * TRANSLATES the history, it does not rewrite it.
   *
   * It auto-extinguishes — each placement rewrites the member's live `enroller`
   * to the canonical account, so the alternate drops out of the chains on its own.
   */
  "~alias"?: string;
  /**
   * Per-user config overrides (§6). Home of `pointsExpirationDays`, which moved
   * off `networkData/basic` when that doc dissolved at the reset (§5). Absent for
   * almost everyone — the rank config is the fallback.
   *
   * ⚠️ `binary` and `rank` are OPTIONAL, and that is load-bearing — not laxity.
   *
   * The sweep (#558) copies this map VERBATIM from `networkData/basic`, so a
   * legacy doc carrying only `binary` produces an override carrying only `binary`.
   * Declaring both required would be a type claiming a guarantee the data never
   * made — and it would license `getPointsExpirationDaysForUser` to resolve the
   * CONTAINER instead of the KEY, which returns `undefined` for the missing side
   * instead of falling through to the rank config. That `undefined` does not throw:
   * it poisons the points expiration date. Keep them optional so the compiler keeps
   * forcing the per-key fallback.
   */
  overrides?: {
    pointsExpirationDays?: {
      binary?: number;
      rank?: number;
    };
  };
  // Written by the #558 sweep, read ONLY by the #557 prune's gate. It is a
  // migration artifact: no production code path reads it, and nothing should
  // start to — it becomes vestigial the moment the prune has run.
  resetSweptAt?: FirebaseFirestore.Timestamp | Date | null;
}

export interface DownlineUser {
  username: string;
  binarySide: userside;
}

export interface RankCarrerData {
  _id: string;
  progress: number | 0;
  status: "current" | "unlocked" | "next" | "locked";
  date: Date;
  priority?: number;
}

export interface UserSubscription {
  username: string;
  subscriptionId: string;
  startDate?: Date | number | FirebaseFirestore.Timestamp;
  lastPayDate?: Date | number | FirebaseFirestore.Timestamp;
  endDate?: Date | number | FirebaseFirestore.Timestamp;
  isActive?: boolean;
  daysLeft?: number;
  isFirstTime?: boolean;
}

export interface UserSubStatus {
  subscriptionId: string;
  endDate?: Date | number;
  isActive?: boolean;
  remainingDays?: number;
}

export interface NetworkData {
  basic: NetworkDataBasic;
}

/** Furthest-out subscription end date covering one binary leg, and who set it. */
export interface BinaryLegExpiration {
  endDate: Date | firestore.firestore.Timestamp;
  username: string;
}

/**
 * Per-leg binary qualification. Both legs must carry a future `endDate` for
 * `binaryActive` (System/Functions.ts) — otherwise matched points are LOST.
 * Fed by trading-academy activations on the enroller's node (GTRAcademy.ts).
 * Moved from `networkData/basic` to the `binaryTree` node at the reset (§3/§5).
 *
 * Both legs are OPTIONAL because the stored document accumulates them ONE AT A
 * TIME: an activation qualifies the leg the recruit landed on and writes only
 * that key, with `set(..., { merge: true })` — a deep merge, which is what keeps
 * the other leg alive. So "only `left` present" is the normal shape for anyone
 * who has just one qualified leg, and post-reset every node starts with the
 * whole field ABSENT (§20.1-6). Declaring the keys required would be a type
 * claiming a guarantee Firestore never made.
 */
export interface BinaryExpiration {
  left?: BinaryLegExpiration;
  right?: BinaryLegExpiration;
}

export interface NetworkDataBasic {
  binaryPercentage: number;
  binarySide: userside;
  currentRank: string;
  enroller: string;
  innerLegUnlocked: boolean | undefined;
  uplines: Array<string>;
  binaryExpiration: BinaryExpiration | undefined;
  pointsExpirationDays?: {
    binary: number;
    rank: number;
  };
}

export interface NetworkDataScholarship {
  periodStartDate: Date | firestore.firestore.Timestamp;
  periodEndDate: Date | firestore.firestore.Timestamp;
  //periodDurationDays: number,
  periodPoints: number;
  nextPeriods: Array<{
    startDate: Date | firestore.firestore.Timestamp;
    endDate: Date | firestore.firestore.Timestamp;
  }>;
}

export interface DirectAffiliatesInterface {
  qualifiedVolume: {
    total: number; // Qualified Volume Points, excluding excess points
    left: number;
    right: number;
  };
  points: {
    current: number; // Total points generated in the current rank
    qualifying: number; // Volume Points granted to the enroller
    //spare: number;
    left: number;
    right: number;
    lost: {
      left: number;
      right: number;
    };
  };
  directsCount: number;
  directsActivated: number;
  liteDirectsActivated: number;
  invoicedTotal: number;
  notes?: Map<string, any>;
  rankPointsHistory?: {
    dateUpdated: Date | firestore.firestore.Timestamp;
    lastUpdatedPeriod: Date | firestore.firestore.Timestamp;
  };
  gtrLifeBonusDetermined?: {
    lastPeriod: string;
    dateUpdated: Timestamp;
  };
}

/**
 * The write-back shape for points flows on the unilevelTree node (§5 cutover).
 *
 * The node absorbed the old `directAffiliates` doc, so it now holds fields from
 * two owners: placeMember owns the structure (`parent`/`uplines`/…) and the 4
 * directs counters (FieldValue.increment at placement); the points engine owns
 * the volume. A flow that read the whole doc and wrote it back with a spread
 * would (a) stomp a counter increment queued by placeMember in the SAME
 * transaction with the stale absolute value it read, or (b) wipe the structure
 * entirely on a set without merge. Every points write-back goes through this
 * picker instead: volume fields only, always with { merge: true }.
 */
export function volumeFields(da: DirectAffiliatesInterface) {
  return {
    qualifiedVolume: da.qualifiedVolume,
    points: da.points,
    invoicedTotal: da.invoicedTotal,
    ...(da.notes !== undefined ? { notes: da.notes } : {}),
    ...(da.rankPointsHistory !== undefined
      ? { rankPointsHistory: da.rankPointsHistory }
      : {}),
    ...(da.gtrLifeBonusDetermined !== undefined
      ? { gtrLifeBonusDetermined: da.gtrLifeBonusDetermined }
      : {}),
  };
}

/**
 * Volume view of a unilevelTree node with the missing pieces defaulted.
 *
 * A node fresh from placeMember (or the migration) carries structure + counters
 * but NO volume fields yet — those appear with the first points write-back. The
 * legacy directAffiliates doc always had them, so several readers dereference
 * `points.lost` / `qualifiedVolume.total` unguarded and would crash on every
 * freshly-placed sponsor. Run reads through this instead.
 */
export function volumeDefaults(
  data: Partial<DirectAffiliatesInterface> | undefined
): DirectAffiliates {
  const base = new DirectAffiliates();
  const merged = { ...base, ...(data ?? {}) } as DirectAffiliates;
  merged.qualifiedVolume = data?.qualifiedVolume ?? base.qualifiedVolume;
  merged.points = data?.points ?? base.points;
  merged.points.lost = merged.points.lost ?? { left: 0, right: 0 };
  merged.directsCount = data?.directsCount ?? 0;
  merged.directsActivated = data?.directsActivated ?? 0;
  merged.liteDirectsActivated = data?.liteDirectsActivated ?? 0;
  merged.invoicedTotal = data?.invoicedTotal ?? 0;
  return merged;
}

export class DirectAffiliates implements DirectAffiliatesInterface {
  qualifiedVolume: {
    total: number;
    left: number;
    right: number;
  };
  points: {
    current: number;
    qualifying: number;
    left: number;
    right: number;
    lost: {
      left: number;
      right: number;
    };
  };
  notes?: Map<string, any>;
  directsCount: number;
  directsActivated: number;
  liteDirectsActivated: number;
  invoicedTotal: number;
  rankPointsHistory?: {
    dateUpdated: Date | firestore.firestore.Timestamp;
    lastUpdatedPeriod: Date | firestore.firestore.Timestamp;
  };
  gtrLifeBonusDetermined?: {
    lastPeriod: string;
    dateUpdated: Timestamp;
  };

  constructor() {
    this.qualifiedVolume = {
      total: 0,
      left: 0,
      right: 0,
    };
    this.points = {
      current: 0,
      qualifying: 0,
      left: 0,
      right: 0,
      lost: {
        left: 0,
        right: 0,
      },
    };
    this.directsCount = 0;
    this.directsActivated = 0;
    this.liteDirectsActivated = 0;
    this.invoicedTotal = 0;
  }
}
export interface BinaryTreeNetworkDataBasicDoc {
  _usernamePK: string;
  parents: Array<string>;
}
export class BinaryTreeNodeUser {
  public loadedLevels = 0;
  public username = "";
  public nickname = "";
  public enroller = "";
  public parent = "";
  public rank = "";
  public pictureUrl = "";
  public points: {
    left: { pending: number; paid: number };
    right: { pending: number; paid: number };
  } = { left: { paid: 0, pending: 0 }, right: { paid: 0, pending: 0 } };
  public childLeft?: BinaryTreeNodeUser | string | null;
  public childRight?: BinaryTreeNodeUser | string | null;
  public subscriptions?: Array<UserSubStatus> | null;
  public binarySide: userside | null;

  constructor();
  constructor(username?: string, parentUsername?: string);
  constructor(username: string, parentUsername: string);
  constructor(username = "", parentUsername = "") {
    this.username = username;
    this.parent = parentUsername;
    this.binarySide = null;
    this.childLeft = null;
    this.childRight = null;
  }
}

export class BinaryTreeNode {
  public id = undefined;
  public enroller = "";
  public parent = "";
  public binarySide: userside;
  public points: BinaryPoints = {
    left: { paid: 0, pending: 0, lost: 0 },
    right: { paid: 0, pending: 0, lost: 0 },
  };
  public hasMatchingPoints: boolean = false;
  public childLeft?: BinaryTreeNodeUser | string | null;
  public childRight?: BinaryTreeNodeUser | string | null;
  // Denormalized binary ancestry index, stored DIRECTLY on the node (aligned with
  // unilevelTree/{u}.uplines). Replaces the old
  // binaryTree/{u}/binaryTreeNetworkData/binaryTreeBasicDoc subdoc for the NEW
  // tree. Optional because legacy `binary-tree` nodes never carried it and the
  // apex node has no ancestry.
  public parents?: Array<string>;
  public notes?: Map<string, any>;
  //constructor();
  constructor(enroller: string, parent: string, binarySide: userside) {
    this.enroller = enroller || "";
    this.parent = parent || "";
    this.binarySide = binarySide;
    this.childLeft = null;
    this.childRight = null;
    this.points = {
      left: { paid: 0, pending: 0, lost: 0 },
      right: { paid: 0, pending: 0, lost: 0 },
    };
  }
}

export interface ClassroomInfoResponse {
  url: string;
  username: string;
  password: string;
  loginUrl: string;
}

export interface TelegramBotUserData {
  userId: number;
  firstName?: string;
  lastName?: string;
  username?: string;
  phoneNumber?: string;
  linkedAt?: Date;
}

export interface SearchUserResponse extends UnilevelMember {
  coverPictureUrl?: string;
  city?: string;
  state?: string;
  country?: string;
  contact: Contact;
  metadata?: Metadata;
  telegramBotUserData?: TelegramBotUserData;
}

export interface Metadata {
  creationTime: string;
  lastSignInTime: string;
  lastRefreshTime?: string | null;
}
export interface Contact {
  email: EmailData;
  mobile?: MobileData;
  telegram?: NumberData;
  whatsapp?: NumberData;
  facebook?: StringData;
  instagram?: StringData;
  tiktok?: StringData;
}

interface Visible {
  visible: boolean;
}
interface StringData extends Visible {
  data: string;
}

interface ModifiedBy {
  modifiedBy?: string;
}

interface EmailData extends StringData, ModifiedBy {}
interface NumberData extends Visible {
  data: {
    dialCode: string;
    number: string;
    userId?: number;
  };
}

interface MobileData extends NumberData {
  whatsapp: boolean;
  telegram: boolean;
}
export interface Address {
  city?: string;
  country?: string;
  neighborhood?: string;
  state?: string;
  street?: string;
  zipCode?: string;
}
